import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Card, Collapse, Row, Col, Statistic, Typography, Skeleton, Input, Progress } from "antd";
import { TrophyOutlined, TeamOutlined, ReloadOutlined, SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { coreAxios } from "../../../utilities/axios";
import * as XLSX from "xlsx";

const { Panel } = Collapse;
const { Title, Text } = Typography;

// ScholarshipTable Component
const ScholarshipTable = ({ title, dataSource }) => (
  <Table
    dataSource={dataSource}
    columns={[
      { title: "#", dataIndex: "serial", key: "serial" },
      { title: "Roll", dataIndex: "rollNumber", key: "rollNumber" },
      { title: "Name", dataIndex: "name", key: "name" },
      { title: "Institute", dataIndex: "institute", key: "institute" },
      { title: "Marks", dataIndex: "totalMarks", key: "totalMarks" },
      {
        title: "Grade",
        dataIndex: "grade",
        key: "grade",
        render: (grade) => (
          <span
            className={`font-semibold ${
              grade === "Talentpool Grade"
                ? "text-green-600"
                : grade === "General Grade"
                ? "text-blue-600"
                : "text-gray-600"
            }`}>
            {grade}
          </span>
        ),
      },
      { title: "Phone", dataIndex: "phone", key: "phone" },
      {
        title: "Position",
        dataIndex: "position",
        key: "position",
        render: (position) => <span className="font-bold">{position}</span>,
      },
    ]}
    rowKey="serial"
    pagination={false}
    bordered
    size="small"
    className="mb-4"
  />
);

// InstituteSummaryTable Component
const InstituteSummaryTable = ({ dataSource }) => (
  <Table
    dataSource={dataSource}
    columns={[
      { title: "#", dataIndex: "serial", key: "serial" },
      { title: "Institute Name", dataIndex: "name", key: "name" },
      {
        title: "Talentpool",
        dataIndex: "talentpool",
        key: "talentpool",
        render: (count) => (
          <span className="text-green-600 font-semibold">{count}</span>
        ),
      },
      {
        title: "General Grade",
        dataIndex: "general",
        key: "general",
        render: (count) => (
          <span className="text-blue-600 font-semibold">{count}</span>
        ),
      },
      {
        title: "Total Students",
        dataIndex: "total",
        key: "total",
        render: (count) => <span className="font-bold">{count}</span>,
      },
    ]}
    rowKey="serial"
    pagination={false}
    bordered
    size="small"
    className="mb-4"
  />
);

// Map API class names (e.g. "Six", "Ten") to numbers for sorting and grade logic
const CLASS_NAME_TO_NUMBER = {
  Two: 2,
  Three: 3,
  Four: 4,
  Five: 5,
  Six: 6,
  Seven: 7,
  Eight: 8,
  Nine: 9,
  Ten: 10,
  Eleven: 11,
  Twelve: 12,
};
const getClassNumber = (instituteClass) => {
  if (instituteClass == null) return NaN;
  const s = String(instituteClass).trim();
  const n = parseInt(s, 10);
  if (!Number.isNaN(n)) return n;
  return CLASS_NAME_TO_NUMBER[s] ?? NaN;
};

const CLASS_NUMBER_TO_NAME = {
  2: "Two", 3: "Three", 4: "Four", 5: "Five", 6: "Six", 7: "Seven",
  8: "Eight", 9: "Nine", 10: "Ten", 11: "Eleven", 12: "Twelve",
};
const getClassDisplayName = (classNum) => {
  const n = classNum != null ? Number(classNum) : NaN;
  return CLASS_NUMBER_TO_NAME[n] ?? String(classNum ?? "Unknown");
};

// Normalize institute name for merging similar spellings (e.g. "Razabari Adarsha High School" vs "Razabari Adarsha High School.")
const normalizeInstituteKey = (name) => {
  if (name == null) return "";
  return String(name)
    .replace(/\r\n|\r|\n/g, " ")
    .replace(/\u00A0/g, " ")
    .trim()
    .replace(/^[.,;:\s]+|[.,;:\s]+$/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
};
const buildInstituteCanonicalMap = (students) => {
  const byNormalized = {};
  (students || []).forEach((s) => {
    const inst = s.institute ?? "Unknown";
    let norm = normalizeInstituteKey(inst);
    if (norm.includes("razabari") && norm.includes("adarsha") && norm.includes("high school")) {
      norm = "razabari adarsha high school";
    }
    if (norm.includes("razabari") && norm.includes("adorsho") && norm.includes("high school")) {
      norm = "razabari adorsho high school";
    }
    if (!byNormalized[norm]) byNormalized[norm] = {};
    byNormalized[norm][inst] = (byNormalized[norm][inst] || 0) + 1;
  });
  const map = {};
  Object.entries(byNormalized).forEach(([, variants]) => {
    const canonical = Object.entries(variants).sort((a, b) => b[1] - a[1])[0][0];
    Object.keys(variants).forEach((orig) => {
      map[orig] = canonical;
    });
  });
  return map;
};

// Helper function to calculate positions within a class
const calculateClassPositions = (students) => {
  if (!students || students.length === 0) return [];

  // Sort by marks descending
  const sorted = [...students].sort((a, b) => b.totalMarks - a.totalMarks);

  // Assign positions (same for same marks)
  let currentPosition = 1;
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i].totalMarks !== sorted[i - 1].totalMarks) {
      currentPosition = i + 1;
    }
    sorted[i].position = currentPosition;
    sorted[i].serial = i + 1;
  }

  return sorted;
};

// ResultDetails Component
const ResultDetails = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [classGroups, setClassGroups] = useState({});
  const [instituteSummary, setInstituteSummary] = useState([]);
  const [totalSearches, setTotalSearches] = useState(0);
  const [juniorSearch, setJuniorSearch] = useState("");
  const [seniorSearch, setSeniorSearch] = useState("");

  useEffect(() => {
    fetchScholarshipInfo();
    fetchTotalSearches();
  }, []);

  const fetchTotalSearches = async () => {
    try {
      const response = await coreAxios.get(`/total-searches`);
      if (response?.status === 200) {
        setTotalSearches(response.data.totalSearches);
      }
    } catch (error) {
      console.error("Error fetching total searches:", error);
    }
  };

  const fetchScholarshipInfo = async () => {
    try {
      setLoading(true);
      const response = await coreAxios.get(`/scholarship-info`);
      if (response?.status === 200) {
        const sortedData = response?.data?.sort(
          (a, b) => new Date(b?.submittedAt) - new Date(a?.submittedAt)
        );
        const processedData = processRollData(sortedData);
        setResults(processedData);
        groupStudentsByClass(sortedData);
        prepareInstituteSummary(sortedData);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching rolls:", error);
    }
  };

  // Function to determine scholarship grade based on class and marks
  const getScholarshipGrade = (student) => {
    const totalMarks =
      student.resultDetails?.[0]?.totalMarks ?? student.correctAnswer;
    if (totalMarks == null) return null;

    const classNumber = getClassNumber(student.instituteClass);

    // For classes 3 to 5
    if (classNumber >= 3 && classNumber <= 5) {
      if (totalMarks >= 45 && totalMarks <= 48) {
        return "General Grade";
      } else if (totalMarks >= 49 && totalMarks <= 50) {
        return "Talentpool Grade";
      }
    }
    // For classes 6 to 8
    else if (classNumber >= 6 && classNumber <= 8) {
      if (totalMarks >= 75 && totalMarks < 80) {
        return "General Grade";
      } else if (totalMarks >= 80 && totalMarks <= 100) {
        return "Talentpool Grade";
      }
    }
    // For classes 9 to 10
    else if (classNumber >= 9 && classNumber <= 10) {
      if (totalMarks >= 75 && totalMarks < 80) {
        return "General Grade";
      } else if (totalMarks >= 80 && totalMarks <= 100) {
        return "Talentpool Grade";
      }
    }
    return null;
  };

  // Group students by class
  const groupStudentsByClass = (data) => {
    const groups = {};

    data.forEach((student) => {
      const totalMarks =
        student.resultDetails?.[0]?.totalMarks ?? student.correctAnswer;
      const hasResult = (student.resultDetails && student.resultDetails.length > 0) || totalMarks != null;
      if (hasResult) {
        const className =
          String(student.instituteClass || "").trim() || "Unknown";
        if (!groups[className]) {
          groups[className] = [];
        }
        groups[className].push(student);
      }
    });

    setClassGroups(groups);
  };

  // Prepare institute summary data (merge similar institute names via canonical map)
  const prepareInstituteSummary = (data) => {
    const canonicalMap = buildInstituteCanonicalMap(data || []);
    const instituteMap = {};

    data.forEach((student) => {
      const hasResult =
        (student.resultDetails && student.resultDetails.length > 0) ||
        student.correctAnswer != null;
      if (hasResult) {
        const grade = getScholarshipGrade(student);
        if (!grade) return;

        const key = canonicalMap[student.institute] ?? student.institute ?? "Unknown";
        if (!instituteMap[key]) {
          instituteMap[key] = {
            name: key,
            talentpool: 0,
            general: 0,
            total: 0,
          };
        }

        if (grade === "Talentpool Grade") {
          instituteMap[key].talentpool++;
        } else if (grade === "General Grade") {
          instituteMap[key].general++;
        }

        instituteMap[key].total++;
      }
    });

    const summaryArray = Object.values(instituteMap).map((item, index) => ({
      ...item,
      serial: index + 1,
    }));

    // Sort by total students descending
    summaryArray.sort((a, b) => b.total - a.total);

    setInstituteSummary(summaryArray);
  };

  const generatePDF = () => {
    const doc = new jsPDF("p", "pt", "a4");
    doc.setFontSize(16);
    doc.text("DMF Scholarship Results 2025", 40, 30);
    doc.setFontSize(10);
    doc.setTextColor(100);

    let sequenceNumber = 1;
    const allData = [];

    // Sort classes numerically
    const sortedClasses = Object.keys(classGroups).sort(
      (a, b) => parseInt(a) - parseInt(b)
    );

    sortedClasses.forEach((className) => {
      const classStudents = classGroups[className]
        .map((student) => {
          const grade = getScholarshipGrade(student);
          if (grade) {
            return {
              class: className,
              rollNumber: student.scholarshipRollNumber,
              name: student.name,
              institute: student.institute,
              totalMarks: student.resultDetails?.[0]?.totalMarks ?? student.correctAnswer,
              grade: grade,
              phone: student.phone?.toString().replace(/^(\d)/, "0$1"),
            };
          }
          return null;
        })
        .filter((student) => student !== null);

      // Calculate positions for this class (marks-based only)
      const studentsWithPositions = calculateClassPositions(classStudents).sort(
        (a, b) => b.totalMarks - a.totalMarks
      );

      studentsWithPositions.forEach((student) => {
        allData.push({
          serial: sequenceNumber++,
          class: student.class,
          rollNumber: student.rollNumber,
          name: student.name,
          institute: student.institute,
          totalMarks: student.totalMarks,
          grade: student.grade,
          phone: student.phone,
          position: student.position,
        });
      });
    });

    // Generate the PDF table with sorted data
    doc.autoTable({
      startY: 50,
      theme: "grid",
      headStyles: {
        fillColor: [46, 125, 50],
        textColor: 255,
        fontSize: 10,
      },
      margin: { left: 20, right: 20 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1,
      },
      head: [
        [
          "#",
          "Class",
          "Roll",
          "Name",
          "Institute",
          "Marks",
          "Grade",
          "Phone",
          "Position",
        ],
      ],
      body: allData.map((data) => [
        data.serial,
        data.class,
        data.rollNumber,
        data.name,
        data.institute,
        data.totalMarks,
        data.grade,
        data.phone,
        data.position,
      ]),
    });

    // Add summary statistics
    const summaryY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    // Count scholarship grades
    let generalCount = 0;
    let talentpoolCount = 0;

    allData.forEach((item) => {
      if (item.grade === "General Grade") generalCount++;
      if (item.grade === "Talentpool Grade") talentpoolCount++;
    });

    doc.text(`Total Students: ${allData.length}`, 40, summaryY);
    doc.text(`Talentpool Grade: ${talentpoolCount}`, 40, summaryY + 20);
    doc.text(`General Grade: ${generalCount}`, 40, summaryY + 40);

    doc.save("DMF_Scholarship_Results_2025.pdf");
  };

  const generateExcel = () => {
    const excelData = [];

    // Sort classes numerically
    const sortedClasses = Object.keys(classGroups).sort(
      (a, b) => parseInt(a) - parseInt(b)
    );

    let sequenceNumber = 1;

    sortedClasses.forEach((className) => {
      const classStudents = classGroups[className]
        .map((student) => {
          const grade = getScholarshipGrade(student);
          if (grade) {
            return {
              class: className,
              rollNumber: student.scholarshipRollNumber,
              name: student.name,
              institute: student.institute,
              totalMarks: student.resultDetails?.[0]?.totalMarks ?? student.correctAnswer,
              grade: grade,
              phone: student.phone?.toString().replace(/^(\d)/, "0$1"),
            };
          }
          return null;
        })
        .filter((student) => student !== null);

      // Calculate positions for this class (marks-based only)
      const studentsWithPositions = calculateClassPositions(classStudents).sort(
        (a, b) => b.totalMarks - a.totalMarks
      );

      studentsWithPositions.forEach((student) => {
        excelData.push({
          "SL No": sequenceNumber++,
          Class: student.class,
          "Roll No": student.rollNumber,
          Name: student.name,
          Institute: student.institute,
          Marks: student.totalMarks,
          Grade: student.grade,
          Phone: student.phone,
          Position: student.position,
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scholarship Results");
    XLSX.writeFile(wb, "DMF_Scholarship_Results_2025.xlsx");
  };

  const generateClassWisePDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
    });

    // Sort classes numerically
    const sortedClasses = Object.keys(classGroups).sort(
      (a, b) => parseInt(a) - parseInt(b)
    );

    // Add page for each class
    sortedClasses.forEach((className) => {
      doc.addPage("landscape");

      // Header with larger text
      doc.setFillColor(46, 125, 50); // Dark green
      doc.rect(0, 0, 280, 12, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text(`DMF-Scholarship-2025 - Class ${className}`, 140, 8, {
        align: "center",
        baseline: "middle",
      });

      // Prepare table data
      const classStudents = classGroups[className]
        .map((student) => {
          const grade = getScholarshipGrade(student);
          if (grade) {
            return {
              rollNumber: student.scholarshipRollNumber,
              name: student.name,
              institute: student.institute,
              gender: student.gender,
              phone: student.phone?.toString().replace(/^(\d)/, "0$1"),
              totalMarks: student.resultDetails?.[0]?.totalMarks ?? student.correctAnswer,
              grade: grade,
            };
          }
          return null;
        })
        .filter((student) => student !== null);

      // Calculate positions
      const tableData = calculateClassPositions(classStudents).sort(
        (a, b) => b.totalMarks - a.totalMarks
      );

      // Create table
      doc.autoTable({
        head: [
          [
            "#",
            "Roll No",
            "Name",
            "Institute",
            "Gender",
            "Phone",
            "Marks",
            "Grade",
            "Position",
          ],
        ],
        body: tableData.map((data, index) => [
          index + 1,
          data.rollNumber,
          data.name,
          data.institute,
          data.gender,
          data.phone,
          data.totalMarks,
          data.grade,
          data.position,
        ]),
        startY: 16, // Below header
        margin: { horizontal: 5 }, // Minimal side margins
        styles: {
          fontSize: 10,
          cellPadding: 2.5,
          lineHeight: 7,
          textColor: [0, 0, 0],
          lineWidth: 0.25,
          lineColor: [220, 220, 220],
        },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 28 },
          2: { cellWidth: 70 },
          3: { cellWidth: 110 },
          4: { cellWidth: 20, halign: "center" },
          5: { cellWidth: 28 },
          6: { cellWidth: 20, halign: "center" },
          7: { cellWidth: 30, halign: "center" },
          8: { cellWidth: 20, halign: "center" },
        },
        headStyles: {
          fillColor: [46, 125, 50],
          textColor: 255,
          fontSize: 11,
          cellPadding: 3,
        },
        bodyStyles: {
          valign: "middle",
        },
        didParseCell: (data) => {
          // Color cells based on grade
          if (data.column.dataKey === "grade") {
            if (data.cell.raw === "Talentpool Grade") {
              data.cell.styles.textColor = [0, 128, 0]; // Dark green
            } else if (data.cell.raw === "General Grade") {
              data.cell.styles.textColor = [0, 0, 128]; // Dark blue
            }
          }
        },
        theme: "grid",
      });
    });

    // Remove initial blank page
    if (doc.getNumberOfPages() > 1) {
      doc.deletePage(1);
    }

    // Save with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    doc.save(`DMF-Scholarship-Class-Wise-Results-${timestamp}.pdf`);
  };

  const generateAllStudentsPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
    });

    // Sort classes numerically
    const sortedClasses = Object.keys(classGroups).sort(
      (a, b) => parseInt(a) - parseInt(b)
    );

    // Add page for each class
    sortedClasses.forEach((className) => {
      doc.addPage("landscape");

      // Header with larger text
      doc.setFillColor(46, 125, 50); // Dark green
      doc.rect(0, 0, 280, 12, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text(
        `DMF-Scholarship-2025 - Class ${className} (All Students)`,
        140,
        8,
        {
          align: "center",
          baseline: "middle",
        }
      );

      // Prepare table data for all students
      const tableData = classGroups[className]
        .map((student) => {
          const grade = getScholarshipGrade(student);
          return {
            rollNumber: student.scholarshipRollNumber,
            name: student.name,
            institute: student.institute,
            gender: student.gender,
            phone: student.phone?.toString().replace(/^(\d)/, "0$1"),
            totalMarks: student.resultDetails?.[0]?.totalMarks ?? student.correctAnswer,
            grade: grade || "Not Qualified",
            status: grade ? "Qualified" : "Not Qualified",
          };
        })
        .sort((a, b) => b.totalMarks - a.totalMarks); // Sort by marks descending

      // Calculate positions
      let currentPosition = 1;
      for (let i = 0; i < tableData.length; i++) {
        if (i > 0 && tableData[i].totalMarks !== tableData[i - 1].totalMarks) {
          currentPosition = i + 1;
        }
        tableData[i].position = currentPosition;
        tableData[i].serial = i + 1;
      }

      // Create table
      doc.autoTable({
        head: [
          [
            "#",
            "Roll No",
            "Name",
            "Institute",
            "Gender",
            "Phone",
            "Marks",
            "Grade",
            "Status",
            "Position",
          ],
        ],
        body: tableData.map((data) => [
          data.serial,
          data.rollNumber,
          data.name,
          data.institute,
          data.gender,
          data.phone,
          data.totalMarks,
          data.grade,
          data.status,
          data.position,
        ]),
        startY: 16, // Below header
        margin: { horizontal: 5 }, // Minimal side margins
        styles: {
          fontSize: 8, // Smaller font to fit more data
          cellPadding: 2,
          lineHeight: 7,
          textColor: [0, 0, 0],
          lineWidth: 0.25,
          lineColor: [220, 220, 220],
        },
        columnStyles: {
          0: { cellWidth: 8, halign: "center" },
          1: { cellWidth: 22 },
          2: { cellWidth: 55 },
          3: { cellWidth: 85 },
          4: { cellWidth: 18, halign: "center" },
          5: { cellWidth: 22 },
          6: { cellWidth: 16, halign: "center" },
          7: { cellWidth: 22, halign: "center" },
          8: { cellWidth: 22, halign: "center" },
          9: { cellWidth: 16, halign: "center" },
        },
        headStyles: {
          fillColor: [46, 125, 50],
          textColor: 255,
          fontSize: 10,
          cellPadding: 2,
        },
        bodyStyles: {
          valign: "middle",
        },
        didParseCell: (data) => {
          // Highlight qualified students
          if (
            data.column.dataKey === "grade" &&
            data.cell.raw !== "Not Qualified"
          ) {
            if (data.cell.raw === "Talentpool Grade") {
              data.cell.styles.fillColor = [200, 230, 200]; // Light green
            } else if (data.cell.raw === "General Grade") {
              data.cell.styles.fillColor = [200, 200, 230]; // Light blue
            }
          }
          if (
            data.column.dataKey === "status" &&
            data.cell.raw === "Qualified"
          ) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = [0, 100, 0]; // Dark green
          }
        },
        theme: "grid",
      });
    });

    // Remove initial blank page
    if (doc.getNumberOfPages() > 1) {
      doc.deletePage(1);
    }

    // Save with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    doc.save(`DMF-Scholarship-All-Students-Results-${timestamp}.pdf`);
  };

  const generateInstituteWisePDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
    });

    // Header
    doc.setFillColor(46, 125, 50);
    doc.rect(0, 0, doc.internal.pageSize.width, 14, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text("DMF Scholarship 2025 - Institute Wise Summary", 105, 10, {
      align: "center",
      baseline: "middle",
    });

    // Prepare table data
    const tableData = instituteSummary.map((institute) => ({
      name: institute.name,
      talentpool: institute.talentpool,
      general: institute.general,
      total: institute.total,
    }));

    // Create table
    doc.autoTable({
      startY: 20,
      head: [
        [
          "#",
          "Institute Name",
          "Talentpool",
          "General Grade",
          "Total Students",
        ],
      ],
      body: instituteSummary.map((institute, index) => [
        index + 1,
        institute.name,
        institute.talentpool,
        institute.general,
        institute.total,
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 15, halign: "center" },
        1: { cellWidth: 90 },
        2: { cellWidth: 30, halign: "center" },
        3: { cellWidth: 30, halign: "center" },
        4: { cellWidth: 30, halign: "center" },
      },
      headStyles: {
        fillColor: [46, 125, 50],
        textColor: 255,
        fontSize: 10,
      },
      didParseCell: (data) => {
        // Color grade columns
        if (data.column.dataKey === "talentpool" && data.cell.raw > 0) {
          data.cell.styles.textColor = [0, 128, 0]; // Green
        } else if (data.column.dataKey === "general" && data.cell.raw > 0) {
          data.cell.styles.textColor = [0, 0, 128]; // Blue
        }
        if (data.column.dataKey === "total") {
          data.cell.styles.fontStyle = "bold";
        }
      },
      theme: "grid",
    });

    // Add summary statistics
    const summaryY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    const totalTalentpool = instituteSummary.reduce(
      (sum, inst) => sum + inst.talentpool,
      0
    );
    const totalGeneral = instituteSummary.reduce(
      (sum, inst) => sum + inst.general,
      0
    );
    const grandTotal = instituteSummary.reduce(
      (sum, inst) => sum + inst.total,
      0
    );

    doc.text(`Total Institutes: ${instituteSummary.length}`, 14, summaryY);
    doc.text(`Total Talentpool: ${totalTalentpool}`, 14, summaryY + 15);
    doc.text(`Total General Grade: ${totalGeneral}`, 14, summaryY + 30);
    doc.text(`Grand Total Students: ${grandTotal}`, 14, summaryY + 45);

    // Save with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    doc.save(`DMF-Scholarship-Institute-Summary-${timestamp}.pdf`);
  };

  // Merge similar institute names (e.g. different capitalization) into one canonical name
  const instituteCanonicalMap = useMemo(() => {
    const all = (Object.values(classGroups) || []).flat();
    return buildInstituteCanonicalMap(all);
  }, [classGroups]);

  // Viva Qualified Leaderboard — DO NOT CHANGE without product approval:
  // - Class 2–5 (Junior): correctAnswer + vivaMarks ≥ 47.5
  // - Class 6–10: correctAnswer + vivaMarks ≥ 83; Class 11–12: ≥ 85.
  // Must run before any early return (hooks rules).
  const vivaLeaderboard = useMemo(() => {
    const all = (Object.values(classGroups) || []).flat();
    const correct = (s) => s?.resultDetails?.[0]?.totalMarks ?? s?.correctAnswer ?? 0;
    const viva = (s) => s?.vibaMarks ?? s?.vivaMarks ?? s?.resultDetails?.[0]?.vibaMarks ?? 0;
    const qualified = all.filter((s) => {
      const v = viva(s);
      if (v == null || v === "") return false; // must have vivaMarks
      const c = Number(correct(s)) || 0;
      const total = c + Number(v);
      const classNum = getClassNumber(s.instituteClass);
      const minTotal = classNum >= 2 && classNum <= 5 ? 47.5 : (classNum >= 6 && classNum <= 10 ? 83 : 85); // Junior ≥ 47.5, Class 6–10 ≥ 83, Class 11–12 ≥ 85
      return total >= minTotal;
    });
    const withTotal = qualified.map((s) => ({
      ...s,
      institute: instituteCanonicalMap[s.institute] ?? s.institute ?? "Unknown",
      _correct: Number(correct(s)) || 0,
      _viva: Number(viva(s)) || 0,
      _total: (Number(correct(s)) || 0) + (Number(viva(s)) || 0),
      _classNum: getClassNumber(s.instituteClass),
    }));
    withTotal.sort((a, b) => b._total - a._total);
    const junior = withTotal.filter((s) => s._classNum >= 2 && s._classNum <= 5);
    const senior = withTotal.filter((s) => s._classNum >= 6 && s._classNum <= 12);
    const addRank = (arr) => arr.map((row, i) => ({ ...row, rank: i + 1 }));
    return {
      junior: addRank(junior),
      senior: addRank(senior),
      total: withTotal.length,
      chartCounts: {
        junior: junior.length,
        senior: senior.length,
      },
      byClass: withTotal.reduce((acc, s) => {
        const key = s._classNum;
        if (!Number.isNaN(key)) {
          acc[key] = (acc[key] || 0) + 1;
        }
        return acc;
      }, {}),
    };
  }, [classGroups, instituteCanonicalMap]);

  // Talentpool (green) vs General counts for table header: Junior ≥52.5 = Talentpool, Senior ≥100 = Talentpool
  const vivaGradeCounts = useMemo(() => {
    const jT = vivaLeaderboard.junior.filter((r) => (r._total ?? 0) >= 52.5).length;
    const jG = vivaLeaderboard.junior.length - jT;
    const sT = vivaLeaderboard.senior.filter((r) => (r._total ?? 0) >= 100).length;
    const sG = vivaLeaderboard.senior.length - sT;
    return { juniorTalentpool: jT, juniorGeneral: jG, seniorTalentpool: sT, seniorGeneral: sG };
  }, [vivaLeaderboard]);

  // 100% = written-qualified list: Class 2–5 ≥75% of 45 (≥34), Class 6–12 ≥65% (of 100). Progress = of that list, কত % ভাইভা মার্কস অ্যাড হয়েছে।
  const vivaMarksProgress = useMemo(() => {
    const all = (Object.values(classGroups) || []).flat();
    const written = (s) => {
      const c = s?.correctAnswer ?? s?.resultDetails?.[0]?.totalMarks;
      return c != null && c !== "" ? Number(c) : NaN;
    };
    const hasVivaFilled = (s) => {
      const v = s?.vibaMarks ?? s?.vivaMarks ?? s?.resultDetails?.[0]?.vibaMarks ?? s?.resultDetails?.[0]?.vivaMarks;
      return v != null && v !== "";
    };
    const getClassNum = (s) => getClassNumber(s.instituteClass);
    // Junior (Class 2–5): 75% of 45 = 33.75 → written ≥ 34
    const juniorQualified = all.filter((s) => {
      const n = getClassNum(s);
      if (n < 2 || n > 5) return false;
      const w = written(s);
      return !Number.isNaN(w) && w >= 34;
    });
    // Senior: 65% of 100 = 65
    const seniorQualified = all.filter((s) => {
      const n = getClassNum(s);
      if (n < 6 || n > 12) return false;
      const w = written(s);
      return !Number.isNaN(w) && w >= 65;
    });
    const juniorFilled = juniorQualified.filter(hasVivaFilled);
    const seniorFilled = seniorQualified.filter(hasVivaFilled);
    return {
      junior: {
        total: juniorQualified.length,
        filled: juniorFilled.length,
        percent: juniorQualified.length ? Math.round((juniorFilled.length / juniorQualified.length) * 100) : 0,
      },
      senior: {
        total: seniorQualified.length,
        filled: seniorFilled.length,
        percent: seniorQualified.length ? Math.round((seniorFilled.length / seniorQualified.length) * 100) : 0,
      },
    };
  }, [classGroups]);

  const barClassNumbers = Object.keys(vivaLeaderboard.byClass)
    .map(Number)
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
  const barLabels = barClassNumbers.map((n) => getClassDisplayName(n));
  const barData = {
    labels: barLabels,
    datasets: [
      {
        label: "Students (Qualified)",
        data: barClassNumbers.map((n) => vivaLeaderboard.byClass[n] || 0),
        backgroundColor: "rgba(34, 197, 94, 0.7)",
        borderColor: "#16a34a",
        borderWidth: 1,
      },
    ],
  };

  const handleRefresh = () => {
    fetchScholarshipInfo();
    fetchTotalSearches();
  };

  const buildFinalResultPDF = (group) => {
    const isJunior = group === "junior";
    const list = isJunior ? vivaLeaderboard.junior : vivaLeaderboard.senior;
    const totalCount = list.length;
    const talentpoolCount = isJunior ? vivaGradeCounts.juniorTalentpool : vivaGradeCounts.seniorTalentpool;
    const generalCount = isJunior ? vivaGradeCounts.juniorGeneral : vivaGradeCounts.seniorGeneral;
    const talentpoolThreshold = isJunior ? 52.5 : 100;
    const subTitle = isJunior ? "Junior (Class 2–5)" : "Senior (Class 6–12)";

    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const tableWidth = pageWidth - margin * 2;
    const greenFill = [220, 252, 231];
    const headFill = isJunior ? [22, 163, 74] : [37, 99, 235];

    // Roll full visible; Name ekto choto; rest balanced
    const rankW = 26;
    const rollW = 56;
    const classW = 30;
    const writtenW = 38;
    const vivaW = 30;
    const totalW = 34;
    const gradeW = 52;
    const fixedSum = rankW + rollW + classW + writtenW + vivaW + totalW + gradeW;
    const nameInstituteSum = tableWidth - fixedSum;
    const nameW = Math.floor(nameInstituteSum * 0.36);
    const instituteW = nameInstituteSum - nameW;
    const headers = ["Rank", "Roll", "Name", "Institute", "Class", "Written", "Viva", "Total", "Grade"];
    const colWidths = [rankW, rollW, nameW, instituteW, classW, writtenW, vivaW, totalW, gradeW];

    const getRoll = (r) => String(r.scholarshipRollNumber ?? r.rollNumber ?? r.resultDetails?.[0]?.rollNumber ?? "").trim();
    const toBodyRow = (r) => [
      r.rank ?? "",
      getRoll(r),
      r.name ?? "",
      r.institute ?? "",
      r.instituteClass ?? "",
      String(r._correct ?? ""),
      String(r._viva ?? ""),
      String(r._total ?? ""),
      (r._total ?? 0) >= talentpoolThreshold ? "Talentpool" : "General",
    ];
    const body = list.map(toBodyRow);

    let startY = 24;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("DMF Scholarship 2026 Final Result Sheet", margin, startY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(subTitle, margin, startY + 14);
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total: ${totalCount}  |  Talentpool: ${talentpoolCount}  |  General: ${generalCount}`, margin, startY + 28);
    doc.setTextColor(0, 0, 0);
    startY += 40;

    doc.autoTable({
      head: [headers],
      body,
      startY,
      margin: { left: margin, right: margin },
      tableWidth,
      columnStyles: {
        0: { cellWidth: colWidths[0], halign: "center", overflow: "ellipsize" },
        1: { cellWidth: colWidths[1], halign: "center", overflow: "ellipsize" },
        2: { cellWidth: colWidths[2], overflow: "ellipsize" },
        3: { cellWidth: colWidths[3], overflow: "ellipsize" },
        4: { cellWidth: colWidths[4], halign: "center", overflow: "ellipsize" },
        5: { cellWidth: colWidths[5], halign: "center", overflow: "ellipsize" },
        6: { cellWidth: colWidths[6], halign: "center", overflow: "ellipsize" },
        7: { cellWidth: colWidths[7], halign: "center", overflow: "ellipsize" },
        8: { cellWidth: colWidths[8], halign: "center", overflow: "ellipsize" },
      },
      headStyles: { fillColor: headFill, textColor: 255, fontSize: 10, fontStyle: "bold", cellPadding: 3 },
      styles: { fontSize: 9, cellPadding: 3, overflow: "ellipsize" },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.overflow = "visible";
        }
        if (data.section === "body" && data.column.index === 8 && data.cell.raw === "Talentpool") {
          const cells = data.row.cells;
          for (let i = 0; i < headers.length; i++) {
            if (cells[i] && cells[i].styles) cells[i].styles.fillColor = greenFill;
          }
        }
      },
      theme: "grid",
    });

    // New page: Institutes ranked by scholarship count
    doc.addPage();
    startY = 24;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Institutes (Rank-wise by scholarship count)", margin, startY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subTitle, margin, startY + 14);
    startY += 32;

    const instituteStats = {};
    list.forEach((r) => {
      const inst = r.institute ?? "Unknown";
      if (!instituteStats[inst]) {
        instituteStats[inst] = { count: 0, totalMarks: 0 };
      }
      instituteStats[inst].count += 1;
      instituteStats[inst].totalMarks += Number(r._total) || 0;
    });
    const sortedInstitutes = Object.entries(instituteStats)
      .sort((a, b) => {
        const [nameA, statsA] = a;
        const [nameB, statsB] = b;
        if (statsB.count !== statsA.count) return statsB.count - statsA.count;
        return statsB.totalMarks - statsA.totalMarks;
      })
      .map(([name, stats], idx) => [
        idx + 1,
        name,
        stats.count,
        (stats.totalMarks / 10).toFixed(1),
      ]);

    const instTableWidth = pageWidth - margin * 2;
    const instRankW = 32;
    const instCountW = 62;
    const instCreditW = 66;
    const instNameW = instTableWidth - instRankW - instCountW - instCreditW;
    doc.autoTable({
      head: [["Rank", "Institute Name", "Scholarship Count", "Credit Point"]],
      body: sortedInstitutes,
      startY,
      margin: { left: margin, right: margin },
      tableWidth: instTableWidth,
      columnStyles: {
        0: { cellWidth: instRankW, halign: "center", overflow: "ellipsize" },
        1: { cellWidth: instNameW, overflow: "ellipsize" },
        2: { cellWidth: instCountW, halign: "center", overflow: "ellipsize" },
        3: { cellWidth: instCreditW, halign: "left", overflow: "ellipsize" },
      },
      headStyles: { fillColor: headFill, textColor: 255, fontSize: 10, fontStyle: "bold", cellPadding: 3 },
      styles: { fontSize: 9, cellPadding: 3, overflow: "ellipsize" },
      didParseCell: (data) => {
        if (data.section === "head") data.cell.styles.overflow = "visible";
      },
      theme: "grid",
    });

    const timestamp = new Date().toISOString().slice(0, 10);
    const suffix = isJunior ? "Junior" : "Senior";
    doc.save(`DMF-Scholarship-2026-Final-Result-Sheet-${suffix}-${timestamp}.pdf`);
  };

  const downloadJuniorFinalResultPDF = () => buildFinalResultPDF("junior");
  const downloadSeniorFinalResultPDF = () => buildFinalResultPDF("senior");

  if (!results && !loading) {
    return (
      <div className="container mx-auto p-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          <Title level={3} className="!mb-0 text-center sm:text-left">DMF Scholarship Result Details 2026</Title>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            রিফ্রেশ
          </Button>
        </div>
        <div className="text-center text-gray-500">No results found. Click refresh to load.</div>
      </div>
    );
  }

  // Count scholarship grades
  const countGrades = () => {
    let generalGradeCount = 0;
    let talentpoolGradeCount = 0;
    let totalStudents = 0;

    Object.values(classGroups).forEach((students) => {
      students.forEach((student) => {
        const grade = getScholarshipGrade(student);
        if (grade === "General Grade") generalGradeCount++;
        if (grade === "Talentpool Grade") talentpoolGradeCount++;
        if (grade) totalStudents++;
      });
    });

    return {
      generalGradeCount,
      talentpoolGradeCount,
      totalStudents,
    };
  };

  const { generalGradeCount, talentpoolGradeCount, totalStudents } =
    countGrades();

  if (loading) {
  return (
    <div className="container mx-auto p-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
          <Title level={3} className="!mb-0 text-center sm:text-left">DMF Scholarship Result Details 2026</Title>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading>
            রিফ্রেশ
          </Button>
        </div>
        <Skeleton active paragraph={{ rows: 2 }} className="mb-4" />
        <div className="mb-4" style={{ height: 280 }}>
          <Skeleton active title={{ width: "40%" }} paragraph={{ rows: 8 }} />
        </div>
        <Row gutter={[16, 16]} className="mb-4">
          <Col span={8}><Skeleton active title={false} paragraph={{ rows: 2 }} /></Col>
          <Col span={8}><Skeleton active title={false} paragraph={{ rows: 2 }} /></Col>
          <Col span={8}><Skeleton active title={false} paragraph={{ rows: 2 }} /></Col>
        </Row>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-5">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-2">
        <div className="text-center sm:text-left">
          <Title level={3} className="!mb-0">DMF Scholarship Result Details 2026</Title>
          <Text type="secondary" className="block mt-1">
            Junior (Class 2–5): ≥ 47.5 — Class 6–10: ≥ 83 — Class 11–12: ≥ 85
          </Text>
        </div>
        <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
          রিফ্রেশ
        </Button>
      </div>
      <div className="mb-6" />

      {/* Viva Qualified Leaderboard — Junior & Senior */}
      <Card
        className="mb-6 border-2 border-green-200"
        title={
          <span className="flex items-center gap-2">
            <TrophyOutlined />
            Viva Qualified Leaderboard (Junior ≥ 47.5, Class 6–10 ≥ 83, Class 11–12 ≥ 85)
          </span>
        }
        extra={
          <span className="flex gap-2">
            <Button type="primary" icon={<DownloadOutlined />} onClick={downloadJuniorFinalResultPDF} size="small" className="bg-green-600 hover:bg-green-700 border-green-600">
              Download Junior PDF
            </Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={downloadSeniorFinalResultPDF} size="small">
              Download Senior PDF
            </Button>
          </span>
        }>
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={8}>
            <Card size="small" className="bg-green-50 border-green-200">
              <Statistic
                title="Total Qualified"
                value={vivaLeaderboard.total}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" className="bg-emerald-50 border-emerald-200">
              <Statistic title="Junior (Class 2–5)" value={vivaLeaderboard.junior.length} />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" className="bg-blue-50 border-blue-200">
              <Statistic title="Senior (Class 6–12)" value={vivaLeaderboard.senior.length} />
            </Card>
          </Col>
        </Row>
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card size="small" title="By Class">
              <div style={{ height: 260 }}>
                <Bar
                  data={barData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true },
                      x: { ticks: { maxRotation: 45 } },
                    },
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
        <Row gutter={[24, 24]} className="mt-4">
          <Col xs={24} lg={12}>
            <div className="mb-3">
              <Text strong className="text-green-800">Junior (৭৫%+ লিখিত): ভাইভা মার্কস কত % অ্যাড হয়েছে</Text>
              <Progress
                percent={vivaMarksProgress.junior.percent}
                status="active"
                strokeColor="#16a34a"
                format={() => `${vivaMarksProgress.junior.filled} / ${vivaMarksProgress.junior.total} (${vivaMarksProgress.junior.percent}%)`}
              />
            </div>
            <Card
              size="small"
              title={<span className="text-green-800 font-semibold">Junior Group (Class 2–5)</span>}
              className="border-green-200"
              extra={
                <span className="text-sm">
                  <span className="font-medium text-green-800">Total: {vivaLeaderboard.junior.length}</span>
                  <span className="mx-2">|</span>
                  <span className="text-green-600 font-medium">Talentpool: {vivaGradeCounts.juniorTalentpool}</span>
                  <span className="mx-2">|</span>
                  <span className="text-blue-600 font-medium">General: {vivaGradeCounts.juniorGeneral}</span>
                </span>
              }>
              <Input
                placeholder="Search all columns..."
                prefix={<SearchOutlined className="text-gray-400" />}
                value={juniorSearch}
                onChange={(e) => setJuniorSearch(e.target.value)}
                allowClear
                className="mb-3"
              />
              <Table
                dataSource={vivaLeaderboard.junior.filter((row) => {
                  if (!juniorSearch.trim()) return true;
                  const q = juniorSearch.trim().toLowerCase();
                  const rank = String(row.rank ?? "");
                  const roll = String(row.scholarshipRollNumber ?? "");
                  const name = String(row.name ?? "");
                  const cls = String(row.instituteClass ?? "");
                  const written = String(row._correct ?? "");
                  const viva = String(row._viva ?? "");
                  const total = String(row._total ?? "");
                  const grade = (row._total ?? 0) >= 52.5 ? "Talentpool" : "General";
                  return [rank, roll, name, cls, written, viva, total, grade].some((s) => s.toLowerCase().includes(q));
                })}
                rowKey={(r) => r.scholarshipRollNumber + (r.rank || 0)}
                pagination={{ pageSize: 10, size: "small" }}
                size="small"
                bordered
                onRow={(record) => ({
                  style: (record._total ?? 0) >= 52.5 ? { backgroundColor: "#dcfce7" } : {},
                })}
                columns={[
                  { title: "Rank", dataIndex: "rank", key: "rank", width: 52, align: "center" },
                  { title: "Roll", dataIndex: "scholarshipRollNumber", key: "roll", width: 72 },
                  { title: "Name", dataIndex: "name", key: "name" },
                  { title: "Class", dataIndex: "instituteClass", key: "class", width: 56, align: "center" },
                  { title: "Written", dataIndex: "_correct", key: "correct", width: 64, align: "center" },
                  { title: "Viva", dataIndex: "_viva", key: "viva", width: 48, align: "center" },
                  { title: "Total", dataIndex: "_total", key: "total", width: 56, align: "center", render: (v) => <strong>{v}</strong> },
                  { title: "Grade", key: "grade", width: 80, align: "center", render: (_, r) => ((r._total ?? 0) >= 52.5 ? <span className="text-green-600 font-medium">Talentpool</span> : <span className="text-blue-600 font-medium">General</span>) },
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <div className="mb-3">
              <Text strong className="text-blue-800">Senior (৬৫%+ লিখিত): ভাইভা মার্কস কত % অ্যাড হয়েছে</Text>
              <Progress
                percent={vivaMarksProgress.senior.percent}
                status="active"
                strokeColor="#2563eb"
                format={() => `${vivaMarksProgress.senior.filled} / ${vivaMarksProgress.senior.total} (${vivaMarksProgress.senior.percent}%)`}
              />
            </div>
            <Card
              size="small"
              title={<span className="text-blue-800 font-semibold">Senior Group (Class 6–12)</span>}
              className="border-blue-200"
              extra={
                <span className="text-sm">
                  <span className="font-medium text-blue-800">Total: {vivaLeaderboard.senior.length}</span>
                  <span className="mx-2">|</span>
                  <span className="text-green-600 font-medium">Talentpool: {vivaGradeCounts.seniorTalentpool}</span>
                  <span className="mx-2">|</span>
                  <span className="text-blue-600 font-medium">General: {vivaGradeCounts.seniorGeneral}</span>
                </span>
              }>
              <Input
                placeholder="Search all columns..."
                prefix={<SearchOutlined className="text-gray-400" />}
                value={seniorSearch}
                onChange={(e) => setSeniorSearch(e.target.value)}
                allowClear
                className="mb-3"
              />
              <Table
                dataSource={vivaLeaderboard.senior.filter((row) => {
                  if (!seniorSearch.trim()) return true;
                  const q = seniorSearch.trim().toLowerCase();
                  const rank = String(row.rank ?? "");
                  const roll = String(row.scholarshipRollNumber ?? "");
                  const name = String(row.name ?? "");
                  const cls = String(row.instituteClass ?? "");
                  const written = String(row._correct ?? "");
                  const viva = String(row._viva ?? "");
                  const total = String(row._total ?? "");
                  const grade = (row._total ?? 0) >= 100 ? "Talentpool" : "General";
                  return [rank, roll, name, cls, written, viva, total, grade].some((s) => s.toLowerCase().includes(q));
                })}
                rowKey={(r) => r.scholarshipRollNumber + (r.rank || 0)}
                pagination={{ pageSize: 10, size: "small" }}
                size="small"
                bordered
                onRow={(record) => ({
                  style: (record._total ?? 0) >= 100 ? { backgroundColor: "#dcfce7" } : {},
                })}
                columns={[
                  { title: "Rank", dataIndex: "rank", key: "rank", width: 52, align: "center" },
                  { title: "Roll", dataIndex: "scholarshipRollNumber", key: "roll", width: 72 },
                  { title: "Name", dataIndex: "name", key: "name" },
                  { title: "Class", dataIndex: "instituteClass", key: "class", width: 56, align: "center" },
                  { title: "Written", dataIndex: "_correct", key: "correct", width: 64, align: "center" },
                  { title: "Viva", dataIndex: "_viva", key: "viva", width: 48, align: "center" },
                  { title: "Total", dataIndex: "_total", key: "total", width: 56, align: "center", render: (v) => <strong>{v}</strong> },
                  { title: "Grade", key: "grade", width: 80, align: "center", render: (_, r) => ((r._total ?? 0) >= 100 ? <span className="text-green-600 font-medium">Talentpool</span> : <span className="text-blue-600 font-medium">General</span>) },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      <div className="text-center mb-8 p-4 bg-green-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">Summary Statistics (All Results)</h2>
        <div className="flex justify-center gap-8">
          <div className="bg-white p-3 rounded shadow">
            <p className="text-gray-600">Total Students</p>
            <p className="text-2xl font-bold">{totalStudents}</p>
          </div>
          <div className="bg-blue-50 p-3 rounded shadow">
            <p className="text-blue-600">General Grade</p>
            <p className="text-2xl font-bold">{generalGradeCount}</p>
          </div>
          <div className="bg-green-50 p-3 rounded shadow">
            <p className="text-green-600">Talentpool Grade</p>
            <p className="text-2xl font-bold">{talentpoolGradeCount}</p>
          </div>
          <div className="bg-purple-50 p-3 rounded shadow">
            <p className="text-purple-600">Total Searches</p>
            <p className="text-2xl font-bold">{totalSearches}</p>
          </div>
        </div>
      </div>

      <div className="text-right mb-5 space-x-2">
        <Button
          onClick={generatePDF}
          type="primary"
          className="bg-blue-600 hover:bg-blue-700">
          Download Combined PDF
        </Button>
        <Button
          onClick={generateExcel}
          type="primary"
          className="bg-green-600 hover:bg-green-700">
          Download Excel
        </Button>
        <Button
          onClick={generateClassWisePDF}
          type="primary"
          className="bg-purple-600 hover:bg-purple-700">
          Class-wise PDF
        </Button>
        <Button
          onClick={generateAllStudentsPDF}
          type="primary"
          className="bg-red-600 hover:bg-red-700"
          style={{ marginLeft: "8px" }}>
          All Students PDF
        </Button>
        <Button
          onClick={generateInstituteWisePDF}
          type="primary"
          className="bg-orange-600 hover:bg-orange-700"
          style={{ marginLeft: "8px" }}>
          Institute Summary PDF
        </Button>
      </div>

      {/* Institute Summary Section */}
      <Card
        title="Institute Wise Summary"
        className="mb-6"
        extra={
          <Button
            onClick={generateInstituteWisePDF}
            type="primary"
            size="small"
            className="bg-orange-600 hover:bg-orange-700">
            Download PDF
          </Button>
        }>
        <InstituteSummaryTable dataSource={instituteSummary} />
      </Card>

      {/* Class-wise Results Section */}
      <Collapse accordion className="mb-6">
        {Object.keys(classGroups)
          .sort((a, b) => getClassNumber(a) - getClassNumber(b))
          .map((className) => {
            const qualifiedStudents = classGroups[className]
              .filter((student) => getScholarshipGrade(student) !== null)
              .map((student) => {
                const grade = getScholarshipGrade(student);
                const totalMarks =
                  student.resultDetails?.[0]?.totalMarks ??
                  student.correctAnswer;
                return {
                  rollNumber: student.scholarshipRollNumber,
                  name: student.name,
                  institute: student.institute,
                  totalMarks,
                  grade: grade,
                  phone: student.phone?.toString().replace(/^(\d)/, "0$1"),
                };
              });

            if (qualifiedStudents.length === 0) return null;

            // Calculate positions for this class (marks-based only)
            const studentsWithPositions = calculateClassPositions(
              qualifiedStudents
            ).sort((a, b) => b.totalMarks - a.totalMarks);

            return (
              <Panel
                header={`Class ${className} (${qualifiedStudents.length} qualified students)`}
                key={className}>
                <ScholarshipTable
                  title={`Class ${className}`}
                  dataSource={studentsWithPositions}
                />
              </Panel>
            );
          })}
      </Collapse>
    </div>
  );
};

// Helper function to process roll data
function processRollData(rollData) {
  const scholarshipListByClass = {};

  rollData?.forEach((item) => {
    if (Array.isArray(item.resultDetails)) {
      const className = `class${item.instituteClass}`;
      if (!scholarshipListByClass[className]) {
        scholarshipListByClass[className] = {
          talentpoolGrade: [],
          generalGrade: [],
        };
      }

      item.resultDetails.forEach((result) => {
        const data = {
          rollNumber: item.scholarshipRollNumber,
          name: item.name,
          institute: item.institute,
          totalMarks: result.totalMarks,
          phone: item.phone,
          grade: "",
        };

        // Class 3-5
        if (item.instituteClass >= 3 && item.instituteClass <= 5) {
          if (result.totalMarks >= 49 && result.totalMarks <= 50) {
            data.grade = "Talentpool Grade";
            scholarshipListByClass[className].talentpoolGrade.push(data);
          } else if (result.totalMarks >= 45 && result.totalMarks <= 48) {
            data.grade = "General Grade";
            scholarshipListByClass[className].generalGrade.push(data);
          }
        }
        // Class 6-8
        else if (item.instituteClass >= 6 && item.instituteClass <= 8) {
          if (result.totalMarks >= 80 && result.totalMarks <= 100) {
            data.grade = "Talentpool Grade";
            scholarshipListByClass[className].talentpoolGrade.push(data);
          } else if (result.totalMarks >= 75 && result.totalMarks < 80) {
            data.grade = "General Grade";
            scholarshipListByClass[className].generalGrade.push(data);
          }
        }
        // Class 9-10
        else if (item.instituteClass >= 9 && item.instituteClass <= 10) {
          if (result.totalMarks >= 80 && result.totalMarks <= 100) {
            data.grade = "Talentpool Grade";
            scholarshipListByClass[className].talentpoolGrade.push(data);
          } else if (result.totalMarks >= 75 && result.totalMarks < 80) {
            data.grade = "General Grade";
            scholarshipListByClass[className].generalGrade.push(data);
          }
        }
      });
    }
  });

  return { scholarshipListByClass };
}

export default ResultDetails;
